import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// Utils
import { isNil } from 'lodash';
import cn from '../../../utils/tools/cn';

// Icons
import { ReactComponent as ChevronUp } from '../../../assets/icons/chevron_up.svg';
import { ReactComponent as ChevronDown } from '../../../assets/icons/chevron_down.svg';

import styles from './TableInputNumber.module.scss';

function TableInputNumber({
  staticValue, value, onChange, isDisabled, isFaded, max, min, withLeftTransition, step, withMathSymbols,
}) {
  const inputRef = useRef();
  const [updatedValue, setUpdatedValue] = useState(null);

  const handleInputClick = () => {
    setUpdatedValue(value);
    inputRef?.current?.focus();
  };

  const handleValidateValue = () => {
    if (value !== updatedValue) {
      onChange(updatedValue).then(() => {
        setUpdatedValue(null);
      });
    } else {
      setUpdatedValue(null);
    }
  };

  const handleInputChange = (e) => {
    const hasPlusSign = e.target.value.includes('+');
    const hasMinusSign = e.target.value.includes('-');

    const val = +(e.target.value.replace('+', '').replace('-', ''));

    if (val === '') {
      setUpdatedValue('');
      return;
    }

    if (Number.isNaN(val)) return;
    if (!isNil(max) && val > max) return;
    if (!isNil(min) && val < min) return;

    let newValue = val;
    if ((updatedValue < 0 || hasMinusSign) && !hasPlusSign) newValue = Math.abs(val) * -1;

    setUpdatedValue(newValue);
  };

  useEffect(() => {
    if (value !== updatedValue) setUpdatedValue(value);
  }, [value]);

  return (
    <div className={cn([styles.input_cell, 'd-flex a-center'])}>
      <button
        type="button"
        className={cn([
          styles.quantity_field,
          'd-flex center m-r-5',
          isFaded ? styles.faded : '',
          withLeftTransition ? styles.with_left_trans : '',
        ])}
        onClick={handleInputClick}
      >
        <div className="d-flex a-center fs-14">
          {!isNil(staticValue) ? (
            <span className="c-neutral-03 m-r-5">{staticValue}</span>
          ) : null}

          {withMathSymbols ? (
            <span
              className={cn([
                styles.math_sign,
                'c-neutral-03 m-r-5 fs-20 lh-1',
                (isNil(updatedValue) ? value : updatedValue) < 0 ? styles.minus : '',
              ])}
            >
              {(isNil(updatedValue) ? value : updatedValue) < 0 ? '-' : '+'}
            </span>
          ) : null}

          <div
            className={cn([
              'd-flex a-center p-relative p-r-5',
            ])}
          >
            <p className={cn([styles.fake_input])}>
              {+((isNil(updatedValue) ? value : updatedValue).toString().replace('-', ''))}
            </p>
            <input
              ref={inputRef}
              type="text"
              className={cn([styles.input])}
              value={+((isNil(updatedValue) ? value : updatedValue).toString().replace('-', ''))}
              onChange={handleInputChange}
              onBlur={handleValidateValue}
            />
          </div>
        </div>
      </button>

      <div className={styles.buttons}>
        <button
          type="button"
          className={cn([styles.arrow, 'd-flex center c-pointer'])}
          onClick={() => onChange(value + step)}
          disabled={isDisabled}
        >
          <ChevronUp width={10} height={10} strokeWidth={2} />
        </button>
        <button
          type="button"
          className={cn([styles.arrow, 'd-flex center c-pointer'])}
          onClick={() => onChange(value - step)}
          disabled={isDisabled}
        >
          <ChevronDown width={10} height={10} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

TableInputNumber.propTypes = {
  staticValue: PropTypes.string,
  value: PropTypes.number,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  isFaded: PropTypes.bool,
  max: PropTypes.number,
  min: PropTypes.number,
  withLeftTransition: PropTypes.bool,
  step: PropTypes.number,
  withMathSymbols: PropTypes.bool,
};

TableInputNumber.defaultProps = {
  staticValue: null,
  value: 0,
  onChange: () => {},
  isDisabled: false,
  isFaded: false,
  max: null,
  min: null,
  withLeftTransition: false,
  step: 1,
  withMathSymbols: false,
};

export default TableInputNumber;
